import { useEffect, useState, useRef } from 'react';
import { Calendar, CreditCard, TrendingDown, Zap } from 'lucide-react';

const stats = [
  { icon: Calendar, value: '2024', suffix: '', label: 'Year Incorporated', isText: true },
  { icon: CreditCard, value: 'SaaS', suffix: '', label: 'Subscription Model', isText: true },
  { icon: TrendingDown, value: 30, suffix: '%', label: 'Lower Dev & Maintenance Costs' },
  { icon: Zap, value: 50, suffix: '%', label: 'Faster Development Cycle' },
];

function AnimatedCounter({ value, suffix, isVisible }) {
  const [count, setCount] = useState(0);

  useEffect(() => {
    if (!isVisible) return;
    
    let start = 0;
    const duration = 2000;
    const increment = value / (duration / 16);
    
    const timer = setInterval(() => {
      start += increment;
      if (start >= value) {
        setCount(value);
        clearInterval(timer);
      } else {
        setCount(Math.floor(start));
      }
    }, 16);

    return () => clearInterval(timer);
  }, [value, isVisible]);

  return (
    <span className="counter-animate">
      {count}{suffix}
    </span>
  );
}

export default function AboutSection() {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.3 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  return (
    <section
      id="about"
      ref={sectionRef}
      data-testid="about-section"
      className="py-24 sm:py-32 bg-[#F4F7FD] relative"
    >
      <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          {/* Left Column - Text */}
          <div className={`reveal ${isVisible ? 'visible' : ''}`}>
            <p className="text-[#F5A623] text-xs tracking-[0.2em] uppercase font-semibold mb-4">
              About Us
            </p>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl tracking-tight font-bold text-[#1A1A2E] mb-6">
              Built by technologists.{' '}
              <span className="text-[#193E82]">Driven by outcomes.</span>
            </h2>
            <p className="text-lg leading-relaxed text-[#5A5A5A] mb-6">
              HireSphere Sdn. Bhd. is a Malaysian-based IT solutions provider and software 
              development company incorporated in April 2024. Founded by a team of accomplished 
              technology professionals, we are committed to delivering cutting-edge, intelligent 
              software solutions.
            </p>
            <p className="text-lg leading-relaxed text-[#5A5A5A]">
              We operate at the intersection of innovation and practicality, building platforms 
              that solve real business problems with AI-first thinking and proven Agile delivery.
            </p>
          </div>

          {/* Right Column - Stats */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            {stats.map((stat, index) => (
              <div
                key={stat.label}
                data-testid={`stat-card-${index}`}
                className={`glass-card-light rounded-2xl p-8 text-center reveal ${
                  isVisible ? 'visible' : ''
                }`}
                style={{ transitionDelay: `${index * 100}ms` }}
              >
                <stat.icon className="w-10 h-10 text-[#193E82] mx-auto mb-4" />
                <div className="text-4xl sm:text-5xl font-extrabold text-[#1A1A2E] mb-2">
                  {stat.isText ? (
                    <span className="counter-animate">{stat.value}</span>
                  ) : (
                    <AnimatedCounter
                      value={typeof stat.value === 'number' ? stat.value : parseInt(stat.value)}
                      suffix={stat.suffix}
                      isVisible={isVisible}
                    />
                  )}
                </div>
                <p className="text-sm text-[#5A5A5A] font-medium">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
